import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScrollLink } from './ScrollLink';
import authService from '../services/authService';
import GoogleSignInButton from './auth/GoogleSignInButton';
import '../styles/Login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = new URLSearchParams(location.search).get('email') || '';
  const registrationSuccess = Boolean(location.state?.registrationSuccess);
  const passwordResetSuccess = Boolean(location.state?.passwordResetSuccess);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(
    registrationSuccess
      ? 'Cadastro realizado com sucesso! Entre para continuar.'
      : (passwordResetSuccess ? 'Senha redefinida com sucesso! Entre com a nova senha.' : '')
  );
  const [messageType, setMessageType] = useState(
    registrationSuccess || passwordResetSuccess ? 'success' : ''
  );

  const finishAuthentication = () => {
    setMessage('Login realizado com sucesso!');
    setMessageType('success');

    setTimeout(() => {
      const previousLocation = location.state?.from;
      const destination = previousLocation?.pathname?.startsWith('/')
        ? `${previousLocation.pathname}${previousLocation.search || ''}`
        : '/meus-cursos';
      navigate(destination, { replace: true });
      window.location.reload();
    }, 600);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    // Validação básica
    if (!email || !password) {
      setMessage('Por favor, preencha todos os campos.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      await authService.login(email, password);
      finishAuthentication();
    } catch (error) {
      setLoading(false);
      setMessage('Erro ao fazer login. Verifique suas credenciais.');
      setMessageType('error');
    }
  };

  const handleGoogleCredential = async (credential) => {
    setMessage('');
    setLoading(true);
    try {
      await authService.googleLogin(credential);
      finishAuthentication();
    } catch (error) {
      setLoading(false);
      setMessage(error.response?.data?.error || 'Não foi possível entrar com o Google.');
      setMessageType('error');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((visible) => !visible);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          {/* Header */}
          <div className="login-header">
            <div className="login-logo">A</div>
            <h1 className="login-title">Bem-vindo de volta!</h1>
            <p className="login-subtitle">
              Entre na sua conta para acessar seus cursos
            </p>
          </div>

          {/* Mensagens */}
          {message && (
            <div className={`${messageType}-message`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {messageType === 'error' ? (
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                ) : (
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                )}
                {messageType === 'error' && (
                  <>
                    <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </>
                )}
                {messageType === 'success' && (
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                )}
              </svg>
              {message}
            </div>
          )}

          <GoogleSignInButton
            onCredential={handleGoogleCredential}
            onError={(text) => {
              setMessage(text);
              setMessageType('error');
            }}
            disabled={loading}
            dividerLabel="ou entre com e-mail"
          />

          {/* Formulário */}
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                E-mail
              </label>
              <input 
                id="email"
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`form-input ${message && messageType === 'error' ? 'error' : ''}`}
                placeholder="Digite seu e-mail"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Senha
              </label>
              <div className="password-field">
                <input 
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`form-input ${message && messageType === 'error' ? 'error' : ''}`}
                  placeholder="Digite sua senha"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="input-icon password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className="forgot-password">
              <ScrollLink to="/recuperacao-senha">
                Esqueceu sua senha?
              </ScrollLink>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="login-button"
            >
              {loading ? (
                <>
                  <div className="loading-spinner-inline"></div>
                  Entrando...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 17L15 12L10 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Entrar
                </>
              )}
            </button>
          </form>

          {/* Link para Cadastro */}
          <div className="signup-link">
            <p>
              Não tem uma conta? <ScrollLink to="/cadastro">Cadastre-se gratuitamente</ScrollLink>
            </p>
          </div>

          {/* Features */}
          <div className="login-features">
            <h3 className="features-title">Por que escolher a ABRALIBER?</h3>
            <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Cursos 100% gratuitos</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Certificados digitais</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Acesso vitalício aos conteúdos</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Suporte dedicado</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
